import * as assignmentActions from '../../../../actions/assignmentActions';
import * as campaignScheduleActions from '../../../../actions/campaignScheduleActions';
import * as contentItemActions from '../../../../actions/contentItemActions';
import * as userActions from '../../../../actions/userActions';
import * as playlistActions from '../../../../actions/playlistActions';
import * as signActions from '../../../../actions/signActions';
import * as sequenceActions from '../../../../actions/sequenceActions';
import * as wizardActions from '../../../../actions/wizardActions';
import Cache from '../utils/Cache';
import LoadHelper from '../utils/LoadHelper';
import Notifications from '../utils/Notifications';
import ASSET_TYPES from '../types/AssetTypes';
import getActiveCampaignId from '../../../../components/CampaignSchedule/utils/getActiveCampaignId';
import { PLAYLIST_REPEAT_VALUES } from '../types/PlaylistValues';

/**
 * @typedef {import('../types/ContentItems').ContentItem} ContentItem
 * @typedef {import('../types/ContentItems').PlaylistContentItem} PlaylistContentItem
 * @typedef {import('../types/ContentItems').SignContentItem} SignContentItem
 * @typedef {import('../types/ContentItems').ContentItemsResponse} ContentItemsResponse
 * @typedef {import('../types/Assignments').Assignment} Assignment
 * @typedef {import('../types/Assignments').Assignments } AssignmentsResponse
 * @typedef {import('../types/CampaignSchedule').CampaignScheduleResponse} CampaignScheduleResponse
 * @typedef {import('../types/User').User} User
 * @typedef {import('../types/Playlist').PlaylistResponse} PlaylistResponse
 * @typedef {import('../types/Sequences').SequencesResponse} SequencesResponse
 * @typedef {import('../types/Sequences').Sequence} Sequence
 * @typedef {import('../types/Sign').Sign} Sign
 * @typedef {import('../types/ScheduleValue').ScheduleValue} ScheduleValue
 */

/**
 * Loads assets based on the current search query.
 * Not cached in wizardData to allow dynamic searching.
 * @param {string} search - The URL search query.
 * @return {Promise<ContentItemsResponse>} The loaded assets.
 * @throws Will throw an error if loading fails.
 */
const loadAssets = async search => {
  const query = LoadHelper.buildAssetsQuery(search);
  const subkey = query;
  const cacheRecord = Cache.getCache(Cache.KEYS.loadAssets, subkey);

  if (cacheRecord) {
    return cacheRecord;
  }

  return contentItemActions
    .fetchContentItems(query)
    .then(async result => {
      let response = /** @type {ContentItemsResponse} */ (result);

      response = LoadHelper.CleanUpFacetFields(response);
      response.content_items = await decorateAssets(response.content_items);

      return Cache.setCache(Cache.KEYS.loadAssets, subkey, response);
    })
    .catch(error => {
      Notifications.error('Failed to fetch assets', error, { search });
      throw error;
    });
};

/**
 * Decorates assets with additional data as needed.
 * @see this.loadAssets
 * @param {ContentItem[]} assets - The assets to decorate.
 * @return {Promise<ContentItem[]>} The decorated assets.
 */
const decorateAssets = async assets => {
  return Promise.all(
    assets.map(async asset => {
      asset.id = asset.content_id;

      switch (asset.type) {
        case ASSET_TYPES.playlist: {
          const playlistAsset = /** @type {PlaylistContentItem} */ (asset);

          return loadPlaylist(playlistAsset.content_id).then(playlist => {
            playlistAsset.files = playlist.files.map(file => {
              file.fileId = file.id;

              return file;
            });
            const parsedPlaylistData = LoadHelper.parsePlaylistData(playlistAsset);

            playlistAsset.width = parsedPlaylistData.width;
            playlistAsset.height = parsedPlaylistData.height;
            playlistAsset.duration = parsedPlaylistData.duration;

            return playlistAsset;
          });
        }

        case ASSET_TYPES.sign:
        case ASSET_TYPES.video:
        case ASSET_TYPES.image:
        default:
          // No additional decoration needed

          return asset;
      }
    }),
  );
};

/**
 * Loads a playlist by content ID.
 * @param {number} id - The content ID of the playlist.
 * @return {Promise<PlaylistResponse>} The loaded playlist data.
 * @throws Will throw an error if loading fails.
 */
const loadPlaylist = async id => {
  const subkey = id;
  const cache = Cache.getCache(Cache.KEYS.loadPlaylist, subkey);

  if (cache) {
    return cache;
  }

  return playlistActions
    .fetchPlaylist(id)
    .then(playlist => {
      return Cache.setCache(Cache.KEYS.loadPlaylist, subkey, playlist);
    })
    .catch(error => {
      Notifications.error('Failed to load playlist', error, { id });
      throw error;
    });
};

/**
 * Loads current user
 * @returns {Promise<User>}
 * @throws Will throw an error if loading fails.
 */
const loadCurrentUser = async () => {
  const subkey = 'current';
  const cache = Cache.getCache(Cache.KEYS.loadCurrentUser, subkey);

  if (cache) {
    return cache;
  }

  return userActions
    .fetchMe()
    .then(result => {
      result.userLocation = result.userLocation || '/';

      return Cache.setCache(Cache.KEYS.loadCurrentUser, subkey, result);
    })
    .catch(error => {
      Notifications.error('Failed to fetch current user', error);
      throw error;
    });
};

/**
 * Loads users.
 * @returns {Promise<User[]>} The loaded users.
 * @throws Will throw an error if loading fails.
 */
const loadUsers = async () => {
  const subkey = 'allUsers';
  const cache = Cache.getCache(Cache.KEYS.loadUsers, subkey);

  if (cache) {
    return cache;
  }

  return userActions
    .fetchUsers()
    .then(result => {
      return Cache.setCache(Cache.KEYS.loadUsers, subkey, result);
    })
    .catch(error => {
      Notifications.error('Failed to fetch users', error);
      throw error;
    });
};

/**
 * Loads campaign schedules.
 * @returns {Promise<CampaignScheduleResponse>} The loaded campaign schedules.
 * @throws Will throw an error if loading fails.
 */
const loadCampaignSchedules = async () => {
  const subkey = 'allSchedules';
  const cache = Cache.getCache(Cache.KEYS.loadCampaignSchedules, subkey);

  if (cache) {
    return cache;
  }

  return campaignScheduleActions
    .fetchCampaignSchedule()
    .then(result => {
      // marking active campaign and cleaning up data for easier use in the wizard
      const activeCampaignId = getActiveCampaignId(result.campaignScheduleEntries);

      result.campaignScheduleEntries = result.campaignScheduleEntries.map(campaign => {
        // marking active campaign
        campaign.isActive = campaign.campaignId === activeCampaignId;

        return campaign;
      });

      return result;
    })
    .then(result => {
      return Cache.setCache(Cache.KEYS.loadCampaignSchedules, subkey, result);
    })
    .catch(error => {
      Notifications.error('Failed to fetch CampaignSchedule', error);
      throw error;
    });
};

/**
 * Loads all sequences
 * @returns {Promise<SequencesResponse>} The loaded sequences.
 * @throws Will throw an error if loading fails.
 */
const loadAllSequences = async () => {
  const query = LoadHelper.setCountParam('', 100000000);
  const subkey = query;
  const cache = Cache.getCache(Cache.KEYS.loadAllSequences, subkey);

  if (cache) {
    return cache;
  }

  return sequenceActions
    .fetchSequences(query)
    .then(sequence => {
      return Cache.setCache(Cache.KEYS.loadAllSequences, subkey, sequence);
    })
    .catch(error => {
      Notifications.error('Failed to load sequences', error);
      throw error;
    });
};

/**
 * Loads a sequence by ID.
 * @param {number} sequenceId - The ID of the sequence to load.
 * @returns {Promise<Sequence|null>} The loaded sequence data, or null if not found.
 * @throws Will throw an error if loading fails.
 */
const loadSequence = async sequenceId => {
  const subkey = sequenceId;
  const cache = Cache.getCache(Cache.KEYS.loadSequence, subkey);

  if (cache) {
    return cache;
  }

  return sequenceActions
    .fetchSequence(sequenceId)
    .then(sequence => {
      return Cache.setCache(Cache.KEYS.loadSequence, subkey, sequence);
    })
    .catch(error => {
      Notifications.error('Failed to load sequence', error, { sequenceId });
      throw error;
    });
};

/**
 * Loads a sign by ID.
 * @param {number} signId - The ID of the sign to load.
 * @return {Promise<SignContentItem>} The loaded sign data.
 * @throws Will throw an error if loading fails.
 */
const loadSign = async signId => {
  const subkey = signId;
  const cache = Cache.getCache(Cache.KEYS.loadSign, subkey);

  if (cache) {
    return cache;
  }

  return signActions
    .fetchSign(signId)
    .then(sign => {
      sign.type = ASSET_TYPES.sign;
      sign.content_id = sign.id;

      return Cache.setCache(Cache.KEYS.loadSign, subkey, sign);
    })
    .catch(error => {
      Notifications.error('Failed to load sign', error, { signId });
      throw error;
    });
};

/**
 * Loads all assignments for a campaign, including sub-folders.
 * @param {number} campaignId - The campaign ID to filter assignments.
 * @param {string} [rootParent] Root Parent  that the query can navigate to
 * @returns {Promise<AssignmentsResponse>} The loaded assignments.
 */
const loadAllAssignments = async (campaignId, rootParent = '/') => {
  const search = LoadHelper.setIncludeSubFoldersParam('', rootParent);

  return loadAssignments(search, campaignId, rootParent);
};

/**
 * Loads assignments based on the current search query.
 * @param {string} search - The URL search query.
 * @param {number} campaignId - The campaign ID to filter assignments.
 * @param {string} [rootParent] Root Parent  that the query can navigate to
 * @returns {Promise<AssignmentsResponse>} The loaded assignments.
 * @throws Will throw an error if loading fails.
 */
const loadAssignments = async (search, campaignId, rootParent = '/') => {
  const query = LoadHelper.buildAssignmentsQuery(search, campaignId, rootParent);
  const subkey = query;
  const cacheRecord = Cache.getCache(Cache.KEYS.loadAssignments, subkey);

  if (cacheRecord) {
    return cacheRecord;
  }

  return assignmentActions
    .fetchAssignments(query)
    .then(result => {
      result.assignments = LoadHelper.normalizeAssignments(result.assignments);

      return Cache.setCache(Cache.KEYS.loadAssignments, subkey, result);
    })
    .catch(error => {
      Notifications.error('Failed to fetch Assignments', error, { search, campaignId });
      throw error;
    });
};

/**
 * Default single-video playlist sequences to Indefinitely as workaround for player freeze
 * https://signstix.atlassian.net/browse/SVT-843
 * @param {Sequence} sequence - The sequence to apply the workaround to.
 * @returns {void}
 */
const bugWorkaround = sequence => {
  if (sequence.assets?.length === 1 && sequence.assets[0].type === ASSET_TYPES.video) {
    sequence.assets[0].repeat = PLAYLIST_REPEAT_VALUES.indefinitely;
    sequence.sequenceEntries[0].repeat = PLAYLIST_REPEAT_VALUES.indefinitely;
    sequence.sequenceEntries[0].asset.repeat = PLAYLIST_REPEAT_VALUES.indefinitely;
  }
};

/**
 * Submits the wizard data.
 * @param {Sequence} sequence - The sequence data to submit.
 * @param {Assignment[]} devices - AssignmentDevices to assign the sequence to.
 * @param {number} campaignId - The campaign ID to associate with the sequence.
 * @param {boolean} deploy - Whether to deploy after submission.
 * @param {ScheduleValue} schedule - The schedule data to associate with the sequence.
 * @returns {Promise<{sequenceId: number}>} Result of submission.
 * @throws Will throw an error if loading fails.
 */
const submitWizard = async (sequence, devices, campaignId, deploy, schedule) => {
  sequence.deployAfterCreation = deploy;

  bugWorkaround(sequence);

  return wizardActions
    .createSequence(sequence, devices, campaignId, schedule)
    .then(result => {
      Notifications.success('Wizard data submitted successfully', { result });

      return result;
    })
    .catch(error => {
      Notifications.error('Failed to submit wizard data', error, { sequence, devices, deploy });
      throw error;
    });
};

export default {
  loadAssets,
  loadUsers,
  loadCurrentUser,
  loadCampaignSchedules,
  loadPlaylist,
  loadAllSequences,
  loadSequence,
  loadSign,
  loadAllAssignments,
  loadAssignments,
  submitWizard,
};
